import type { RoleListQuery } from '@/api/vacancies';

/**
 * **The address contract of §6.8, and the whole of it.**
 *
 * `DESIGN.md` §6.8 is `/vacancies`. The parameter names, the defaulting, the validation and the
 * mapping onto the API's own query are decided **here** and nowhere else — the shape
 * `candidateFilters.ts` established for §6.3.
 *
 * ```
 * /vacancies?page=2
 * ```
 *
 * | In the address bar | On the wire | Why |
 * |---|---|---|
 * | `page` | `page` | §6.8's grid is paginated at 50, the roles route's `DEFAULT_PAGE_SIZE` |
 *
 * ⚠️ **`/vacancies` in the address bar is `/api/v1/roles/` on the wire, and the rename stops at the
 * client.** `backend/CONTEXT.md` pins `Role` as an internal term — *"always means a vacancy and never
 * access rights"* — in a product that also has access `Group`s, so every user-visible string and every
 * address says *vacancy* while the API stays `/api/v1/roles/`. That is the precedent recorded in
 * `src/app/navItems.ts` and applied to a query parameter in `candidateFilters.ts`.
 *
 * ⚠️ **This module owned a second address until Sprint 19, and it is gone rather than moved
 * wholesale** (2026-08-11, ADR 0018). `VACANCY_QUESTIONS_PATH`, `vacancyQuestionsUrl()`,
 * `VACANCY_ID_PARAM`, `BANK_PAGE_SIZE`, `toQuestionBankQuery` and the three question filters lived
 * here on a stated reason — *"both live under one segment, so both live in one module"*. A question
 * stopped belonging to a vacancy, so `/vacancies/:vacancyId/questions` **has no screen and no API
 * behind it**: what survived is the three filters and the page size, which moved to the module that
 * owns `/questions` (`pages/QuestionBank/questionBankUrls.ts`), and the builder and the path constant
 * were deleted rather than re-pointed. The guard in `vacancyGrid.test.tsx` still fails **any** module
 * outside this one that spells `/vacancies` in any of its forms.
 */

/** The screen's own address, so a caller building a link does not restate it. */
export const VACANCIES_PATH = '/vacancies';

/**
 * The one parameter name, as data rather than as a literal in the screen. Reading and writing both go
 * through this object, so a rename is one edit and cannot half-happen.
 */
export const VACANCY_URL_PARAMS = {
  page: 'page',
} as const;

/**
 * The page size §6.8's range sentence does its arithmetic with — 50, the roles route's own
 * `DEFAULT_PAGE_SIZE` (`backend/src/apps/vacancies/api.py`).
 *
 * ⚠️ **It is deliberately *not* sent, which is the opposite of `CANDIDATE_PAGE_SIZE`'s decision, and
 * the reason is a cache entry rather than a style.** `queryKeys.roles(query)` keys on the query **as
 * given** (TASK-070, which recorded this as a wart on purpose: `rolesQuery()` and `rolesQuery({ page: 1
 * })` are two entries for one body). §6.3's `VacancyFilter` and §6.4's `AddCandidateDialog` read the
 * first page under `rolesQuery()` — no query at all — so sending `page_size=50` here would fork a
 * second entry for the identical body and a second request for it on the first page of this grid.
 * `toRoleListQuery` therefore sends **nothing** on page 1 and only `page` after it.
 *
 * ⚠️ **What that costs, stated rather than hidden.** No guard can pin this number: `page_size` is
 * declared in the OpenAPI document with no default (`anyOf: integer | null`), so the server's 50 is not
 * in the schema and a test asserting the two agree would be asserting against itself — the same reason
 * TASK-070 refused a `NINJA_MAX_PER_PAGE_SIZE` constant while pinning the reorder body's `maxItems`,
 * which *was* in the document — ⚠️ that comparison is retracted with the route (ADR 0018, 2026-08-11),
 * and what it illustrated survives: a number the document declares can be pinned and one it does not
 * cannot. If the backend changed its default, the range sentence would misnumber on page 2 and
 * beyond while nothing else broke. The exposure is bounded on purpose: `first` is derived from the page
 * and this size, so on page 1 — the page nearly every reader sees — it is `1` whatever this says, and
 * `last` is always `first + rows.length - 1` from the payload itself.
 */
export const VACANCY_PAGE_SIZE = 50;

/** What the address bar says, after validation. */
export interface VacancyFilters {
  /** One-based, as the API counts pages. */
  page: number;
}

/** What a caller building a link may say. Anything omitted is left out of the address entirely. */
export interface VacancyFilterInput {
  page?: number;
}

/** A positive integer, or nothing. `?page=0`, `?page=-2` and `?page=two` are all "nothing". */
function positiveInteger(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null;
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
}

/**
 * Reads the address bar. **Never throws and never produces a value the API would refuse** — a `page`
 * of `0`, `-2` or `two` is dropped, because the alternative is that an address somebody typed or
 * shortened by hand renders an error state.
 *
 * ⚠️ **A `page` past the end is *kept*, not clamped** — §6.3's own correction, made at Sprint 9's
 * review. `count` is only known once the answer arrives, so clamping would mean the screen rewriting
 * the address from a response: a second writer of the page, a history entry nobody navigated to, and a
 * reader that could no longer be a pure function of `URLSearchParams`. The address stays what was
 * typed, the screen says the page is empty, and *Next* is disabled on an empty page so it cannot walk
 * on for ever.
 */
export function readVacancyFilters(params: URLSearchParams): VacancyFilters {
  return { page: positiveInteger(params.get(VACANCY_URL_PARAMS.page)) ?? 1 };
}

/**
 * Whether anything the viewer chose **could be hiding rows** — which of §8's two empty states an empty
 * page is.
 *
 * §6.8 has one control, so there is one case: a `?page=` past the last page. It is the plainest case
 * there is of *something on screen is hiding rows*, and §6.3 shipped the opposite for one review cycle
 * — *"No candidates yet."* with **Add candidate** over *"Showing 0–0 of 137"*, offering the one action
 * ADR 0002 exists to prevent to somebody whose bookmarked address merely overshot.
 */
export function couldBeHidingVacancies(filters: VacancyFilters): boolean {
  return filters.page > 1;
}

/**
 * The query string for a filter set — what `setSearchParams` is given, and what a link into this screen
 * would be composed from (see the note below). `?page=1` is absent rather than empty: it is noise in an
 * address people copy to each other.
 */
export function vacancySearch(filters: VacancyFilterInput = {}): string {
  const params = new URLSearchParams();
  if (filters.page != null && filters.page > 1) {
    params.set(VACANCY_URL_PARAMS.page, String(filters.page));
  }
  return params.toString();
}

/*
 * ⚠️ **There is deliberately no `vacanciesUrl()` yet**, the `candidatesUrl()` of this contract. Nothing
 * links *into* this screen under a filter today: §6.2's setup path is a **button** and giving it this
 * destination is TASK-077's criterion, not TASK-071's. Writing the builder now would be an export with
 * no caller — Sprint 8's `applyTenantAccent`, which shipped with a green unit test and nothing calling
 * it and was refused twice before being deleted. `VACANCIES_PATH` and `vacancySearch()` are what a
 * caller composes it from, and it belongs in this file the moment it has one.
 */

/**
 * The address bar translated into `GET /api/v1/roles/`'s own query — the **only** place the two
 * vocabularies meet.
 *
 * ⚠️ **Page 1 is `undefined`, not `{ page: 1 }`**, and that is what shares the cache entry §6.3's
 * vacancy filter already holds instead of asking for the same body under a second key
 * (`VACANCY_PAGE_SIZE` above; TASK-070's recorded wart). The two calls send the identical URL, so the
 * shared entry can only ever hold the identical body.
 */
export function toRoleListQuery(filters: VacancyFilters): RoleListQuery | undefined {
  return filters.page > 1 ? { page: filters.page } : undefined;
}
